import { useState } from 'react';
import { Search, Plus, Users, Filter } from 'lucide-react';
import { useContacts, type ContactFilters } from '../hooks/useContacts';
import { Badge } from '../components/shared/Badge';
import { StatusDot } from '../components/shared/StatusDot';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface ContactsViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const roleOptions = ['', 'insured', 'agent', 'underwriter', 'broker', 'adjuster', 'carrier-rep', 'other'];
const statusOptions = ['', 'active', 'inactive', 'lead', 'prospect', 'customer'];

export function ContactsView({ onNavigate }: ContactsViewProps) {
  const [filters, setFilters] = useState<ContactFilters>({ limit: 50, offset: 0 });
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, error } = useContacts({
    ...filters,
    search: searchText || undefined,
  });

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setFilters((f) => ({ ...f, offset: 0, search: searchText }));
  }

  function handleRowClick(contactId: string) {
    onNavigate('contact-detail', { contactId });
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Contacts</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data ? `${data.total} contacts` : 'Manage your contacts'}
          </p>
        </div>
        <button className="btn-primary" onClick={() => onNavigate('contact-detail', { contactId: 'new' })}>
          <Plus className="h-4 w-4" />
          New Contact
        </button>
      </div>

      {/* Filters */}
      <div className="border-b border-surface-200 bg-white px-6 py-3">
        <div className="flex items-center gap-3">
          <form onSubmit={handleSearch} className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search contacts..."
              className="input pl-9"
            />
          </form>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn('btn-secondary btn-sm', showFilters && 'bg-brand-50 border-brand-200 text-brand-700')}
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-3 flex items-center gap-3">
            <div>
              <label className="label mb-1">Role</label>
              <select
                className="input py-1.5"
                value={filters.role ?? ''}
                onChange={(e) => setFilters((f) => ({ ...f, role: e.target.value || undefined, offset: 0 }))}
              >
                <option value="">All Roles</option>
                {roleOptions.filter(Boolean).map((r) => (
                  <option key={r} value={r}>{r.replace('-', ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label mb-1">Status</label>
              <select
                className="input py-1.5"
                value={filters.status ?? ''}
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value || undefined, offset: 0 }))}
              >
                <option value="">All Statuses</option>
                {statusOptions.filter(Boolean).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="px-6 py-4">
        {error ? (
          <div className="card p-8 text-center text-sm text-red-600">
            Failed to load contacts. Please try again.
          </div>
        ) : isLoading ? (
          <div className="card divide-y divide-surface-100">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-3">
                <div className="h-8 w-8 animate-pulse rounded-full bg-surface-200" />
                <div className="flex-1">
                  <div className="h-4 w-40 animate-pulse rounded bg-surface-200" />
                  <div className="mt-1 h-3 w-24 animate-pulse rounded bg-surface-100" />
                </div>
              </div>
            ))}
          </div>
        ) : data && data.contacts.length > 0 ? (
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-header">Name</th>
                  <th className="table-header">Email</th>
                  <th className="table-header">Role</th>
                  <th className="table-header">Organization</th>
                  <th className="table-header">Last Activity</th>
                  <th className="table-header">Tags</th>
                </tr>
              </thead>
              <tbody>
                {data.contacts.map((contact) => (
                  <tr
                    key={contact.id}
                    onClick={() => handleRowClick(contact.id)}
                    className="table-row-hover"
                  >
                    <td className="table-cell">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">
                          {contact.firstName?.[0]}{contact.lastName?.[0]}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">
                            {contact.firstName} {contact.lastName}
                          </div>
                          {contact.title && (
                            <div className="text-xs text-slate-500">{contact.title}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="table-cell text-slate-600">{contact.email ?? '--'}</td>
                    <td className="table-cell">
                      {contact.insuranceRole ? (
                        <Badge color="blue">{contact.insuranceRole}</Badge>
                      ) : (
                        <span className="text-slate-400">--</span>
                      )}
                    </td>
                    <td className="table-cell text-slate-600">
                      {contact.organizationName ?? '--'}
                    </td>
                    <td className="table-cell text-xs text-slate-500">
                      {contact.lastActivityAt
                        ? new Date(contact.lastActivityAt).toLocaleDateString()
                        : '--'}
                    </td>
                    <td className="table-cell">
                      <div className="flex flex-wrap gap-1">
                        {contact.tags?.slice(0, 3).map((tag) => (
                          <Badge key={tag} color="slate" size="sm">{tag}</Badge>
                        ))}
                        {contact.tags && contact.tags.length > 3 && (
                          <Badge color="gray" size="sm">+{contact.tags.length - 3}</Badge>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {data.total > data.limit && (
              <div className="flex items-center justify-between border-t border-surface-200 px-4 py-3">
                <span className="text-xs text-slate-500">
                  Showing {data.offset + 1}-{Math.min(data.offset + data.limit, data.total)} of {data.total}
                </span>
                <div className="flex gap-2">
                  <button
                    className="btn-secondary btn-sm"
                    disabled={data.offset === 0}
                    onClick={() => setFilters((f) => ({ ...f, offset: Math.max(0, (f.offset ?? 0) - (f.limit ?? 50)) }))}
                  >
                    Previous
                  </button>
                  <button
                    className="btn-secondary btn-sm"
                    disabled={data.offset + data.limit >= data.total}
                    onClick={() => setFilters((f) => ({ ...f, offset: (f.offset ?? 0) + (f.limit ?? 50) }))}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <EmptyState
            icon={Users}
            title="No contacts yet"
            description="Get started by adding your first contact or importing from a file."
            actionLabel="Add Contact"
            onAction={() => onNavigate('contact-detail', { contactId: 'new' })}
          />
        )}
      </div>
    </div>
  );
}
